import React, { useEffect } from "react";
import { MapContainer, TileLayer, Marker, Popup, Circle, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { LocateFixed, Star, Wallet } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useI18n } from "../lib/i18n";
import { euro } from "../lib/format";

const LYON = [45.764, 4.8357];

const COLORS = { business: "#D9A441", mission: "#34d399", creator: "#4f6fd6", you: "#f87171" };

const GLYPHS = {
  business: '<path d="M3 9l1-4h16l1 4"/><path d="M5 9v10h14V9"/><path d="M9 19v-5h6v5"/>',
  creator: '<path d="M3 8h4l2-2h6l2 2h4v11H3z"/><circle cx="12" cy="13" r="3.2"/>',
  mission: '<rect x="3" y="8" width="18" height="11" rx="1.5"/><path d="M9 8V6h6v2"/>',
};

function pinIcon(type) {
  const c = COLORS[type] || COLORS.business;
  const glyph = GLYPHS[type] || GLYPHS.business;
  return L.divIcon({
    className: "lc-pin",
    html: `<span style="display:grid;place-items:center;width:32px;height:32px;border-radius:9999px;background:${c};box-shadow:0 2px 6px rgba(0,0,0,.45);border:2px solid #0A1224">
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#fff" stroke-width="2.1" stroke-linecap="round" stroke-linejoin="round">${glyph}</svg></span>`,
    iconSize: [32, 32],
    iconAnchor: [16, 16],
    popupAnchor: [0, -16],
  });
}

const youIcon = L.divIcon({
  className: "lc-pin",
  html: `<span style="display:block;width:18px;height:18px;border-radius:9999px;background:#f87171;box-shadow:0 0 0 6px rgba(248,113,113,.28),0 0 0 1px #0A1224"></span>`,
  iconSize: [18, 18],
  iconAnchor: [9, 9],
});

function Recenter({ center }) {
  const map = useMap();
  useEffect(() => {
    if (center) map.flyTo(center, 13, { duration: 0.8 });
  }, [center, map]);
  return null;
}

function FitOnMount() {
  const map = useMap();
  useEffect(() => {
    const t = setTimeout(() => map.invalidateSize(), 300);
    return () => clearTimeout(t);
  }, [map]);
  return null;
}

export function LiveMap({
  businesses = [],
  missions = [],
  creators = [],
  userPos = null,
  onLocate,
  height = "16rem",
  className = "",
}) {
  const { t } = useI18n();
  const navigate = useNavigate();
  const center = userPos ? [userPos.lat, userPos.lng] : LYON;

  const legend = [
    businesses.length && ["business", t("legendBusiness")],
    missions.length && ["mission", t("legendMission")],
    creators.length && ["creator", t("legendCreator")],
    userPos && ["you", t("legendYou")],
  ].filter(Boolean);

  return (
    <div className={`relative overflow-hidden rounded-2xl border border-cardline ${className}`} style={{ height }} data-testid="live-map">
      <MapContainer center={center} zoom={12} scrollWheelZoom={false} style={{ height: "100%", width: "100%", background: "#0A1224" }} zoomControl={false}>
        <TileLayer
          attribution='&copy; OpenStreetMap'
          className="lc-map-tiles"
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />
        <FitOnMount />
        {userPos && <Recenter center={[userPos.lat, userPos.lng]} />}
        {userPos && (
          <>
            <Circle center={[userPos.lat, userPos.lng]} radius={5000} pathOptions={{ color: "#D9A441", fillColor: "#D9A441", fillOpacity: 0.06, weight: 1 }} />
            <Marker position={[userPos.lat, userPos.lng]} icon={youIcon} />
          </>
        )}
        {businesses.filter((b) => b.lat && b.lng).map((b) => (
          <Marker key={`b-${b.id}`} position={[b.lat, b.lng]} icon={pinIcon("business")}>
            <Popup>
              <div className="text-ink">
                <div className="font-semibold">{b.name}</div>
                <div className="text-xs text-slate-600">{b.category} · ⭐ {b.rating}</div>
                <button onClick={() => navigate(`/businesses/${b.id}`)} className="mt-1 text-xs font-semibold text-amber-600">{t("viewProfile")} →</button>
              </div>
            </Popup>
          </Marker>
        ))}
        {missions.filter((m) => m.lat && m.lng).map((m) => (
          <Marker key={`m-${m.id}`} position={[m.lat, m.lng]} icon={pinIcon("mission")}>
            <Popup>
              <div className="text-ink">
                <div className="font-semibold">{m.title}</div>
                <div className="mt-0.5 flex items-center gap-1 text-xs text-slate-600">
                  <Wallet size={12} /> {m.reward_value || euro(m.budget)} · {m.city}
                </div>
                <button onClick={() => navigate("/campaigns")} className="mt-1 text-xs font-semibold text-amber-600">
                  {t("apply")} →
                </button>
              </div>
            </Popup>
          </Marker>
        ))}
        {creators.filter((c) => c.lat && c.lng).map((c) => (
          <Marker key={`c-${c.id}`} position={[c.lat, c.lng]} icon={pinIcon("creator")}>
            <Popup>
              <div className="text-ink">
                <div className="font-semibold">{c.name}</div>
                <div className="text-xs text-slate-600">{c.categories?.slice(0, 2).join(" · ")}</div>
                <button onClick={() => navigate(`/creators/${c.id}`)} className="mt-1 flex items-center gap-1 text-xs font-semibold text-fuchsia-600">
                  <Star size={11} /> {t("viewProfile")} →
                </button>
              </div>
            </Popup>
          </Marker>
        ))}
      </MapContainer>

      {onLocate && (
        <button
          onClick={onLocate}
          data-testid="map-locate-btn"
          className="absolute right-3 top-3 z-[500] flex items-center gap-1.5 rounded-full bg-gold px-3 py-2 text-xs font-bold text-ink shadow-lg"
        >
          <LocateFixed size={14} /> {t("locateMe")}
        </button>
      )}

      {legend.length > 0 && (
        <div className="absolute bottom-3 left-3 z-[500] flex flex-wrap gap-2 rounded-xl bg-ink/80 px-3 py-2 text-[11px] backdrop-blur">
          {legend.map(([type, label]) => (
            <span key={type} className="flex items-center gap-1.5 text-slate-200">
              <span className="h-2.5 w-2.5 rounded-full" style={{ background: COLORS[type] }} /> {label}
            </span>
          ))}
        </div>
      )}
    </div>
  );
}
